/**
 * Canonical AP archive filename contract (frontend copy).
 *
 *   V{voucher}_{vendor}_{invoice}_{MM.DD.YYYY}[_FA].pdf
 *
 * Mirrors APArchiveBE/src/utils/naming.js. There used to be two competing
 * formats in this app - this one, and a VENDOR_INVOICE_YYYYMMDD_V123 variant in
 * lib/utils.js that the backend router could not recognise. Files built the
 * second way silently landed in Uploaded/ instead of Invoices/{year}/. Keep this
 * file and its backend twin in step.
 */

// Strip whitespace, accents, and anything OneDrive/Windows rejects.
export function sanitizeSegment(value) {
  return String(value ?? '')
    .replace(/\s+/g, '')
    // NFKD splits accented letters into base + combining mark; the final
    // [^\w.-] strip then drops the marks, so "Beliveau" survives as ASCII.
    .normalize('NFKD')
    .replace(/[\\/:*?"<>|#%]/g, '')
    .replace(/[^\w.-]/g, '');
}

// '2023-11-01' or a Date -> '11.01.2023'
export function toFileDate(value) {
  if (!value) return 'unknown';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const [y, m, d] = value.slice(0, 10).split('-');
    return `${m}.${d}.${y}`;
  }
  const dt = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return 'unknown';
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${m}.${d}.${dt.getFullYear()}`;
}

// '11.01.2023' -> '2023-11-01', for prefilling <input type="date">
export function fileDateToIso(fileDate) {
  const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(String(fileDate || ''));
  return m ? `${m[3]}-${m[1]}-${m[2]}` : '';
}

/**
 * Normalizes whatever shape a date arrives in to 'YYYY-MM-DD' for a date input.
 *
 * Deliberately avoids `new Date(x).toISOString()`: that converts to UTC, so a
 * local-parsed date west of Greenwich comes back a day early, and a bare
 * '2023-11-01' parsed as UTC reads back a day late through local getters.
 * An invoice date is a calendar date, so read the components directly.
 */
export function toIsoDate(value) {
  if (!value) return '';
  const text = String(value).trim();

  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  const us = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})/.exec(text);
  if (us) {
    return `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;
  }

  const d = new Date(text);
  if (Number.isNaN(d.getTime())) return '';
  const utc = /Z$|[+-]00:?00$/.test(text);
  const y = utc ? d.getUTCFullYear() : d.getFullYear();
  const m = String((utc ? d.getUTCMonth() : d.getMonth()) + 1).padStart(2, '0');
  const day = String(utc ? d.getUTCDate() : d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export const MAX_FILENAME_LENGTH = 200;

export function buildArchiveFileName({
  voucherNumber,
  vendor,
  invoiceNumber,
  invoiceDate,
  fixedAsset
}) {
  const voucher = sanitizeSegment(voucherNumber);
  const vendorPart = sanitizeSegment(vendor) || 'UnknownVendor';
  const invoicePart = sanitizeSegment(invoiceNumber) || 'UnknownInvoice';
  const datePart = toFileDate(invoiceDate);
  const faPart = fixedAsset ? '_FA' : '';
  return `V${voucher}_${vendorPart}_${invoicePart}_${datePart}${faPart}.pdf`.slice(
    0,
    MAX_FILENAME_LENGTH
  );
}

export const ARCHIVE_NAME_RE =
  /^V(\d+)_([A-Za-z0-9.-]+)_([A-Za-z0-9.-]+)_(\d{2})\.(\d{2})\.(\d{4})(_FA)?$/i;

export function parseArchiveFileName(filename) {
  const base = String(filename || '').replace(/\.pdf$/i, '');
  const m = ARCHIVE_NAME_RE.exec(base);
  if (!m) return null;
  const [, voucher, vendor, invoice, month, day, year, fa] = m;
  return {
    voucherNumber: voucher,
    vendor,
    invoiceNumber: invoice,
    invoiceDate: `${year}-${month}-${day}`,
    fileDate: `${month}.${day}.${year}`,
    year,
    fixedAsset: Boolean(fa)
  };
}

export function isArchiveFileName(filename) {
  return /^V\d{3,}/i.test(String(filename || ''));
}
